import { useMemo } from 'react';
import { OptionKey, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos/generated-paths';
import type { ICategorySpendLimitDto } from '@/dtos/models';

const QUERY_KEY = 'useGetCategorySpendLimitsQueryKey';

export interface CategorySpendLimit {
  id: number;
  policyId: number;
  categoryType: string;
  categorySpendLimit: number;
  totalSpent: number;
}

const getFormattedData = (data: ICategorySpendLimitDto[]): CategorySpendLimit[] => {
  return data.map((limit) => ({
    id: limit.id || 0,
    policyId: limit.policy_id || 0,
    categoryType: limit.category_type || '',
    categorySpendLimit: limit.category_spend_limit || 0,
    totalSpent: limit.total_spent || 0,
  }));
};

interface Props {
  accountId?: string;
  periodFrom?: OptionKey;
}

const useGetCategorySpendLimitsQuery = ({ accountId, periodFrom }: Props) => {
  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY, accountId, periodFrom],
    () => {
      return api.get<ICategorySpendLimitDto[]>(API_URL.CATEGORY_SPEND_LIMITS, {
        params: {
          account_id: accountId,
          period_from: dayjs(periodFrom).format(RAW_DATE_FORMAT),
        },
      });
    },
    {
      enabled: Boolean(accountId) && Boolean(periodFrom),
    },
  );

  const formattedData = useMemo(() => getFormattedData(data?.data || []), [data]);

  return {
    categorySpendLimits: formattedData,
    isSuccess,
    isLoading,
  };
};

export default useGetCategorySpendLimitsQuery;
